import { CountryCurrencyMapping } from '@dupe/common/types'

export const IS_PREVIEW = process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview'
export const IS_PROD = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
export const IS_DEV =
  process.env.NEXT_PUBLIC_VERCEL_ENV === 'development' ||
  process.env.NODE_ENV === 'development'

export const RECAPTCA_ID = 'phoneRecaptcha'

/**
 * Auth-modal `source` set by the results-page sign-up wall. The post-auth
 * onboarding (`components/results/onboarding`) keys off this so the
 * results-specific flow renders instead of the default onboarding — and so the
 * modal widens for the two-column layout. Kept here (not in the wall component)
 * so both the wall and `AuthContent` import the same literal.
 */
export const RESULTS_SIGNUP_WALL_SOURCE = 'results-signup-wall'

/**
 * Desktop modal width (px) for the brand-led onboarding — the `test` arm of the
 * `visual-onboarding-variant` experiment. The brand flow is single-column and
 * caps its content at `max-w-[480px]` (`BrandTasteOnboardingResults`), and the
 * modal panel applies `width` with no extra horizontal padding, so the shell
 * hugs the content exactly at 480. Shared by `AuthModal` (real flow) and
 * `BrandTasteOnboardingPreview` (`?previewBrandOnboarding=1`) so both match.
 * The `control` arm keeps its wider two-column 900px, set inline in `AuthModal`.
 */
export const BRAND_ONBOARDING_MODAL_WIDTH = 480

export const DUPE_SUPPORTED_COUNTRIES_MAPPING: CountryCurrencyMapping = {
  us: {
    name: 'US',
    value: 'us',
    flag: '🇺🇸',
    currency_symbols: ['USD', 'US$', '$'],
  },
  ca: {
    name: 'CA',
    value: 'ca',
    flag: '🇨🇦',
    currency_symbols: ['C', 'CAD', 'C$', 'CAD$', '$'],
  },
  gb: {
    name: 'UK',
    value: 'gb',
    flag: '🇬🇧',
    currency_symbols: ['£', '€'],
  },
  au: {
    name: 'AU',
    value: 'au',
    flag: '🇦🇺',
    currency_symbols: ['A', 'A$', '$'],
  },
  nz: {
    name: 'NZ',
    value: 'nz',
    flag: '🇳🇿',
    currency_symbols: ['N', 'NZD', 'NZ$', '$'],
  },
}

export const TEMP_USER_ID_KEY = '__dupe_temp_user_id'
export const DUPE_AUTH_USER_ID_KEY = '__dupe_auth_user_id'
export const DUPE_SESSION_COOKIE_KEY = '__dupe_session'
export const DUPE_FLAGS_COOKIE_KEY = '__dupe_flags'
/**
 * Carries the set of feature-flag keys whose `$feature_flag_called` exposure
 * event must keep flowing to PostHog (running-experiment flags + deliberate
 * keeps). Computed server-side in middleware from the PostHog experiments API
 * and read at `posthog.init` to configure the `before_send` filter. See
 * `posthogExperimentFlags.ts` / `posthogBrowserConfig.ts`.
 */
export const DUPE_EXPOSURE_FLAGS_COOKIE_KEY = '__dupe_exposure_flags'
export const MAX_AGE_OF_COOKIE = 60 * 60 * 24 * 365 // one year in seconds

export const IMAGEKIT_URL_ENDPOINT = 'https://assets.dupe.com/'
export const IMAGEKIT_PUBLIC_KEY = process.env.NEXT_PUBLIC_IMAGEKIT_PUBLIC_KEY
export const IMAGEKIT_PRIVATE_KEY = process.env.IMAGEKIT_PRIVATE_KEY

// For new Imagekit account
export const IMAGEKIT_DUPE_URL_ENDPOINT = 'https://ik.imagekit.io/dupe/'
export const IMAGEKIT_DUPE_PUBLIC_KEY =
  process.env.NEXT_PUBLIC_IMAGEKIT_DUPE_PUBLIC_KEY
export const IMAGEKIT_DUPE_PRIVATE_KEY = process.env.IMAGEKIT_DUPE_PRIVATE_KEY

export const DEFAULT_LOADING_COPY = [
  'Detecting product',
  'Assessing similarities',
  'Scanning partner sites',
  'Removing duplicates',
  'Doing one more pass',
  'This is a tough product',
  'Hang tight, friend!',
]

export const PRODUCT_RESULTS_PER_PAGE = 24

// Product identifiers
export const SEE_ALL_PRODUCTS_IDENTIFIER = 'see-all-products'

// Research time saved stats
/** Multiplier from actual agent time to equivalent human research time. 5 min agent ≈ 2 days human. */
export const RESEARCH_HUMAN_TIME_MULTIPLIER = 576
export const RESEARCH_BASE_HUMAN_MS = 0
export const RESEARCH_DONE_STATUSES = new Set([
  'complete',
  'researched',
  'published',
  'failed',
  'cancelled',
])
